import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { dfpHistoricalRevenueStatementResponse } from '@interfaces/dfp/response/dfpRevenueStatementResponse';
import { PdfUtilsServiceV2 } from '@src/app/core/services/helpers/pdf-utils/pdf-utils-v2.service';
import { TableAbstractComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/table-abstract/table-abstract.component';
import { TableExportService } from '@src/app/shared/components/table/services/table-export.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { DfpPanelComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/dfp-panel/dfp-panel.component';
import { RevenueOriginDialogComponent } from '@src/app/features/dashboard/performance/pages/dfp/components/revenue-origin-dialog/revenue-origin-dialog.component';
import { MenuModule } from 'primeng/menu';
import { PanelModule } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { DfpColumnWidthDirective } from '@app/features/dashboard/performance/pages/dfp/components/table-abstract/dfp-column-width.directive';
import { DfpClipGuardDirective } from '@app/features/dashboard/performance/pages/dfp/components/table-abstract/dfp-clip-guard.directive';

interface RevenueStatementHistorical {
  year: number;
  value: number;
}

interface GroupByRevenueStatement {
  month: number;
  hasSubItems: boolean;
  historic: RevenueStatementHistorical[];
}

@Component({
  selector: 'app-table-revenue-statement',
  templateUrl: './table-revenue-statement.component.html',
  styleUrls: [
    '../table-abstract/table-abstract.component.scss',
    './table-revenue-statement.component.scss',
  ],
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    TableModule,
    PanelModule,
    MenuModule,
    DfpPanelComponent,
    RevenueOriginDialogComponent,
    DfpColumnWidthDirective,
    DfpClipGuardDirective,
  ],
})
export class TableRevenueStatementComponent extends TableAbstractComponent {
  @Input() companyName: string = '';

  /**
   * Empresa para o (i) "De onde vem cada mês" (card 3700). Nula no modo somente leitura do parceiro: sem o ícone e
   * sem chamada ao endpoint da DFP (o parceiro lê pelo endpoint do deal).
   */
  @Input() originCompanyId: string | null = null;

  /** Uma ação no modal "De onde vem cada mês" mudou o que vale: a tela recarrega Faturamento e a conferência da DRE. */
  @Output() originChanged = new EventEmitter<void>();
  override tableData: dfpHistoricalRevenueStatementResponse[] = [];
  groupedRevenueStatement: GroupByRevenueStatement[] = [];

  constructor(pdfUtilsService: PdfUtilsServiceV2, tableExportService: TableExportService) {
    super(pdfUtilsService, tableExportService);
  }

  override groupTableData(): void {
    const groupedRevenueStatement = this.tableData.reduce((acc, sales) => {
      let group = acc.find((group: GroupByRevenueStatement) => group.month === sales.month);
      if (!group) {
        group = {
          month: sales.month,
          hasSubItems: false,
          historic: [],
        };
        acc.push(group);
      }

      let yearGroup = group.historic.find(y => y.year === sales.year);
      if (!yearGroup) {
        yearGroup = {
          year: sales.year,
          value: sales.value,
        };
        group.historic.push(yearGroup);
      } else {
        yearGroup.value = sales.value;
      }

      return acc;
    }, [] as GroupByRevenueStatement[]);

    this.groupedRevenueStatement = groupedRevenueStatement.sort((a, b) => {
      if (a.month === 0) return 1;
      if (b.month === 0) return -1;
      return a.month - b.month;
    });
  }

  getValueByYear(sales: RevenueStatementHistorical[], year: number): number | string {
    if (!sales || sales.length === 0) return 0;

    if ('years' in sales[0]) {
      for (const sale of sales as {
        name?: string;
        years?: RevenueStatementHistorical[];
      }[]) {
        const yearData = sale.years?.find(y => y.year === year);
        if (yearData) return yearData.value;
      }
    } else {
      const yearData = (sales as RevenueStatementHistorical[]).find(y => y.year === year);
      if (yearData) return yearData.value;
    }

    return 0;
  }

  override exportToPdf(): void {
    this.exportPerformanceTableToPdf({
      filename: 'declaracao-faturamento.pdf',
      title: 'Declaração de Faturamento',
      subtitle: this.companyName,
    });
  }

  override exportToExcel(): void {
    this.exportPerformanceTableToExcel({
      selector: '.performance-table--revenue',
      sheetName: 'Faturamento',
      filenamePrefix: 'declaracao-faturamento',
    });
  }
}
